import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { devWarn } from '../lib/devLog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HTTP_UNAUTHORIZED = 401;

// Module-level constants (API, HTTP_UNAUTHORIZED, axios, navigate result) are
// intentionally NOT in dependency arrays below — they are stable across renders.

const useAdminAuth = ({ redirectOnAuthFail = true } = {}) => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      await axios.get(`${API}/admin/me`, { withCredentials: true });
      setAuthed(true);
    } catch (err) {
      setAuthed(false);
      if (redirectOnAuthFail && err.response?.status === HTTP_UNAUTHORIZED) {
        navigate('/admin');
      }
    } finally {
      setChecking(false);
    }
  }, [navigate, redirectOnAuthFail]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
    } catch (err) {
      // Non-fatal — server may be unreachable, but we still want to clear
      // client state and redirect.
      devWarn('admin logout request failed:', err.message);
    }
    setAuthed(false);
    navigate('/admin');
  }, [navigate]);

  return { checking, authed, logout, checkAuth };
};

export { HTTP_UNAUTHORIZED, API };
export default useAdminAuth;
